import { Injectable } from '@angular/core';
import { fabric } from 'fabric';
import { TranslateService } from '@ngx-translate/core';
import { Textbox } from 'fabric/fabric-impl';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ZoomUtil } from '../common/utils/zoom.util';
import { TextSettingsDirector } from '../sessions/session/context-menus/fabric-object-settings';
import { ObjectDuplicationUtilsService } from '../utilities/object-duplication-utils.service';
import { IFrameUtils } from '../utilities/iframe.utils';
import {
  AppConfigurationService,
  CollaborativeApps,
} from '../sessions/session/iframe/additional-apps.utils';
import {
  ensureAllObjectAreLive,
  getFabricObjectWithAbsolutePosition,
} from '../sessions/session/wb-canvas/fabric-utils';
import {
  BoardItemsManager,
  FabricObject,
} from '../sessions/session/items-canvas/board-items/board-items-manager';
import { AppsInsertionService } from '../sessions/session/items-canvas/board-items/apps-insertion.service';
import { isBoardItem } from '../sessions/common/y-session';
import { YSessionBoardItemsAdapter } from '../sessions/common/y-session-board-items-adapter.service';
import { IconMessageToasterElement } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { INFOS } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { BoardItemsInteractionsManager } from '../sessions/session/items-canvas/board-items-interactions-manager';
import { SpaceRepository } from '../state/space.repository';
import { ItemModel } from '../models/itemModel';
import { customFabricFields } from '../common/interfaces/sync-service-interface';
import { BoardItemsStackingOrderManager } from '../sessions/session/items-canvas/board-items-stacking-order-manager';
import { MainToolbarService } from '../sessions/session/wb-main-toolbar-v2/services/main-toolbar.service';
import { DomListenerFactoryService } from './dom-listener-factory.service';
import { KeyboardShortcutsCheckService } from './keyboard-shortcuts-check.service';
import {
  NotificationDataBuilder,
  NotificationToasterService,
  NotificationType,
} from './notification-toaster.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { DocumentUploadService } from './document-upload.service';
import { SlidesImportFlowService } from './slides-import/slides-import-flow.service';
import { ObserverModeService } from './observer-mode.service';

export interface PasteLocation {
  x: number;
  y: number;
}

// we store both pasteLocation and mouseLocation to handle the case of pasting an object multiple times with same mouse location
interface PasteActionData {
  pasteLocation: { x: number; y: number }; // actual paste location
  mouseLocation: { x: number; y: number }; // mouse location when the paste action was performed
}

export const PASTE_LOCATION_OFFSET = 40;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class WbCopyPasteService {
  copiedSelection: any = {};
  objectOffset = { x: 0, y: 0 };
  lastPointer = { x: 0, y: 0 };

  private copiedObjects: FabricObject[] = [];

  private domListener;

  private disableKeyEventsSubscription?: Subscription;
  private lastPasteActionData: PasteActionData = {
    pasteLocation: { x: 0, y: 0 },
    mouseLocation: { x: 0, y: 0 },
  };

  constructor(
    public sharedDataService: SessionSharedDataService,
    private objectDuplicationUtilsService: ObjectDuplicationUtilsService,
    private zoomUtil: ZoomUtil,
    private iframeUtils: IFrameUtils,
    private ySessionAdapter: YSessionBoardItemsAdapter,
    private translate: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private domListenerFactoryService: DomListenerFactoryService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private spaceRepo: SpaceRepository,
    public documentUploadService: DocumentUploadService,
    private boardItemsStackingOrderManager: BoardItemsStackingOrderManager,
    private mainToolbarService: MainToolbarService,
    private keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
    private slidesImportFlowService: SlidesImportFlowService,
    private observerModeService: ObserverModeService,
    private appConfigurationService: AppConfigurationService,
    private appsInsertionService: AppsInsertionService,
  ) {
    this.domListener = this.domListenerFactoryService.createInstance();
  }

  private subscribeToDisableKeyEvents() {
    this.disableKeyEventsSubscription?.unsubscribe();
    this.disableKeyEventsSubscription = this.sharedDataService.isDisableKeyEvents$
      .pipe(untilDestroyed(this))
      .subscribe((isDisabledKeyEvents) => {
        if (isDisabledKeyEvents) {
          this.removeHandlers();
        } else {
          this.initHandlers();
        }
      });
  }

  public initService() {
    this.subscribeToDisableKeyEvents();
    this.initHandlers();
  }

  public destroyService() {
    this.removeHandlers();
    this.disableKeyEventsSubscription?.unsubscribe();
  }

  public initHandlers() {
    this.removeHandlers();
    this.addCopyPasteHandler();
    this.addDuplicationHandler();
  }

  public removeHandlers() {
    this.domListener.clear();
  }

  public duplicateActiveObject() {
    const activeObject = this.sharedDataService.fabricCanvas?.getActiveObject();
    // skip duplication if there is no active object
    if (!activeObject) {
      return;
    }
    // to handle the case of duplicating a sticky note while in edit mode
    // because in that case the active object is the textbox of the sticky note not the sticky note itself
    // so we need to set the parent board item as the active object to duplicate the sticky note with its content
    if (activeObject && (activeObject as any).parentBoardItem) {
      this.sharedDataService.fabricCanvas?.discardActiveObject();
      this.sharedDataService.fabricCanvas?.setActiveObject((activeObject as any).parentBoardItem);
    }
    this.handleCopy();
    this.handlePaste(true);
  }

  addDuplicationHandler(): void {
    this.domListener.add(
      document,
      'keydown',
      (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
          event.preventDefault();
          this.duplicateActiveObject();

          event.stopPropagation();
        }
      },
      true,
    );
  }

  addCopyPasteHandler() {
    this.domListener.add(window, 'copy', (event: ClipboardEvent) => {
      const handledClipboard = this.handleCopyClipboardData(event);
      this.handleCopy(event);

      // Only stop propagation if we are actively handling whiteboard copy to avoid breaking native copy elsewhere
      if (handledClipboard) {
        event.stopImmediatePropagation();
      }
    });

    this.domListener.add(window, 'paste', (event: ClipboardEvent) => {
      if (!this.shouldHandlePaste()) {
        return;
      }
      // handle paste on whiteboard
      this.handlePaste(false, event);
    });
  }

  handleCopy(event?: ClipboardEvent): void {
    if (!this.canCopy() || !this.sharedDataService.fabricCanvas) {
      return;
    }

    let activeObjects = this.getActiveObjectsWithCurrentStackingOrder();

    activeObjects = activeObjects.filter(
      (o) => !o.excludeFromExport && (!isBoardItem(o) || o.duplicatable),
    );

    if (activeObjects.length === 0) {
      return;
    }

    const clonedObjectsWithAbsolutePosition = activeObjects.map((obj) =>
      // To ensure that we don't modify the original objects on the canvas
      // And For objects copied and pasted in patches, the position will be relative to the group, so we need the absolute one
      getFabricObjectWithAbsolutePosition(obj as FabricObject),
    );

    this.clearClipBoardDataIfNoTextBoxIsSelected(event, activeObjects);
    this.handlePartialSelectionOfTextBoxIfSelected(event, clonedObjectsWithAbsolutePosition);

    this.copiedObjects = clonedObjectsWithAbsolutePosition;

    this.copiedSelection = this.sharedDataService.fabricCanvas?.getActiveObject();
  }

  async handlePaste(
    isDuplicationPaste: boolean = false,
    event?: ClipboardEvent,
    position?: PasteLocation,
  ): Promise<void> {
    if (
      !this.copiedObjects ||
      !this.sharedDataService.fabricCanvas ||
      !this.spaceRepo.activeSpaceSelectedBoardUid ||
      !this.copiedSelection
    ) {
      return;
    }

    if (!this.canPasteOnWhiteboard()) {
      this.showCanNotPasteNotification();
      return;
    }

    if (
      !isDuplicationPaste &&
      (await this.handlePasteFromClipboard(event, this.copiedObjects, position))
    ) {
      return;
    }

    const selectionCenter = this.copiedSelection?.getCenterPoint?.() ?? {
      x: (this.copiedSelection?.left ?? 0) + (this.copiedSelection?.width ?? 0) / 2,
      y: (this.copiedSelection?.top ?? 0) + (this.copiedSelection?.height ?? 0) / 2,
    };

    const pasteLocation = position ?? this.getPasteLocation(isDuplicationPaste);

    this.ModifyObjectsPositionAccordingToPastePosition(
      this.copiedObjects,
      pasteLocation,
      selectionCenter,
      isDuplicationPaste,
    );

    // For fabric objects to work properly, we need to convert them to an object then enliven them, cloning then adding directly will cause some functionalities to break
    // For example selecting text in a text box will break for some reason.
    const fabricObjectsConvertedToObjects = this.copiedObjects.map((copiedObject) => {
      // In the backend, we didn't change how we store items, there are still some stored as FrameItem and some as Object
      if (isBoardItem(copiedObject) && copiedObject.isStoredAsFrameItem()) {
        return copiedObject.toFrameItem();
      } else {
        return copiedObject.toObject(customFabricFields);
      }
    });
    const parsedObjects = this.ySessionAdapter.parseYObjectsAndYCanvasObjects(
      fabricObjectsConvertedToObjects,
      this.spaceRepo.activeSpaceSelectedBoardUid,
    );
    ensureAllObjectAreLive(parsedObjects, (liveObjects) => {
      const canvas = this.sharedDataService.fabricCanvas;
      if (!canvas || !liveObjects.length) {
        return;
      }

      // Making sure that copied objects are all above all the current objects on the board
      this.boardItemsStackingOrderManager.setObjectsNewStackingOrder(liveObjects);

      // TODO <Hatahet> instead make the object responsible for providing its clone
      for (const liveObject of liveObjects) {
        // Cloned objects should have different ids than original ones so that they will be handled independently
        liveObject.uid = uuidv4();
        liveObject.skipRemoteAddition = undefined;
      }

      this.sharedDataService.fabricCanvas?.add(...liveObjects);

      // Select the newly pasted objects
      if (liveObjects.length > 1) {
        const activeSelection = new fabric.ActiveSelection(liveObjects, { canvas });
        canvas.discardActiveObject();
        activeSelection.setObjectsCoords();
        canvas.setActiveObject(activeSelection);
      } else {
        canvas.setActiveObject(liveObjects[0]);
      }
      canvas.requestRenderAll();

      // When re-pasting without copying, we paste the last pasted items
      this.copiedSelection = canvas.getActiveObject();
    });
  }

  // calculate the paste location
  // In case of duplication paste, we use the object duplication utils service to get the paste location
  // In case of normal paste, we use the last mouse location to get the paste location
  // If re-pasting with same mouse location, we offset the paste location by the PASTE_LOCATION_OFFSET
  private getPasteLocation(isDuplicationPaste: boolean): { x: number; y: number } {
    let pasteLocation = { x: 0, y: 0 };

    if (isDuplicationPaste) {
      pasteLocation = this.objectDuplicationUtilsService.getPasteLocation(
        this.copiedSelection,
        this.sharedDataService.fabricCanvas,
      );
      return pasteLocation;
    }

    pasteLocation = this.zoomUtil.getCenterCoord();

    // If the user's pointer is not over the whiteboard canvas, fallback to the center of the VPT
    if (!this.sharedDataService.isPointerOverWhiteboardCanvas) {
      return pasteLocation;
    }

    if (this.sharedDataService.lastMouseLocation?.pointer) {
      pasteLocation.x = this.sharedDataService.lastMouseLocation?.pointer.x;
      pasteLocation.y = this.sharedDataService.lastMouseLocation?.pointer.y;
    }

    if (
      this.sharedDataService.lastMouseLocation?.pointer &&
      this.arePointsEqual(
        this.sharedDataService.lastMouseLocation?.pointer,
        this.lastPasteActionData.mouseLocation,
      )
    ) {
      pasteLocation = {
        x: this.lastPasteActionData.pasteLocation.x + PASTE_LOCATION_OFFSET,
        y: this.lastPasteActionData.pasteLocation.y + PASTE_LOCATION_OFFSET,
      };
    }

    this.lastPasteActionData.pasteLocation = pasteLocation;
    this.lastPasteActionData.mouseLocation = this.sharedDataService.lastMouseLocation?.pointer ?? {
      x: 0,
      y: 0,
    };

    return pasteLocation;
  }

  private arePointsEqual(
    point1: { x: number; y: number },
    point2: { x: number; y: number },
  ): boolean {
    return point1.x === point2.x && point1.y === point2.y;
  }

  public getCanvasPasteLocationFromClientPosition(clientPosition: PasteLocation): PasteLocation {
    const pointer = this.sharedDataService.fabricCanvas?.getPointer({
      clientX: clientPosition.x,
      clientY: clientPosition.y,
    } as MouseEvent);
    return pointer ? { x: pointer.x, y: pointer.y } : this.zoomUtil.getCenterCoord();
  }

  // This is used to get the selected objects with the same stacking order as they are in the canvas
  // Because using canvas.getActiveObjects() returns them in the order of selection
  private getActiveObjectsWithCurrentStackingOrder() {
    if (!this.sharedDataService.fabricCanvas) {
      return [];
    }
    const activeObject = this.sharedDataService.fabricCanvas.getActiveObject();

    return this.sharedDataService.fabricCanvas
      .getObjects()
      .filter((o) => o === activeObject || o.group === activeObject);
  }

  ModifyObjectsPositionAccordingToPastePosition(
    copiedObjects: FabricObject[],
    pastePosition: { x: number; y: number },
    selectionCenter: { x: number; y: number },
    isDuplicationPaste: boolean,
  ) {
    return copiedObjects.map((obj) => {
      if (!pastePosition) {
        return obj;
      }

      if (obj.left === undefined || obj.top === undefined) {
        return obj;
      }

      // Changing the position to be relative to the paste location instead of the original copy location
      const diffToCenter = {
        x: obj.left - selectionCenter.x,
        y: obj.top - selectionCenter.y,
      };
      const newLeft = (pastePosition?.x || 0) + diffToCenter.x;
      const newTop = (pastePosition?.y || 0) + diffToCenter.y;

      // Compute translation delta relative to previous position
      const deltaX = newLeft - obj.left;
      const deltaY = newTop - obj.top;

      obj.left = newLeft;
      obj.top = newTop;

      if (isBoardItem(obj)) {
        obj.handleInitializationFromPaste(deltaX, deltaY);
      }

      return obj;
    });
  }

  handleCopyClipboardData(event: ClipboardEvent): boolean {
    if (!this.canCopy()) {
      return false;
    }
    // only write text object to clipboard if we don't have anything copied or text selected
    const textSelected = window.getSelection()?.toString();
    // remove selection
    if (textSelected) {
      this.writeTextToClipboard(event, textSelected);
      window.getSelection()?.removeAllRanges();
      return true;
    }
    const itemSelected = this.boardItemsInteractionsManager.selectedObject;
    if (itemSelected && (itemSelected as Textbox).text && itemSelected.type !== 'IMAGE') {
      const elementText = (itemSelected as Textbox).text as string;
      const target = (event.target as HTMLTextAreaElement) || (event as any).srcElement;
      const selectionStart = (target && (target as any)?.selectionStart) ?? 0;
      const selectionEnd = (target && (target as any)?.selectionEnd) ?? elementText.length;
      const selected = elementText.substring(selectionStart, selectionEnd);
      this.writeTextToClipboard(event, selected);
      return true;
    }
    return false;
  }

  // Prefer synchronous event clipboard when available (better Safari compatibility)
  private writeTextToClipboard(event: ClipboardEvent, text: string): void {
    if (event.clipboardData) {
      event.clipboardData.setData('text/plain', text);
      event.preventDefault();
      return;
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  async handlePasteFromClipboard(
    event: ClipboardEvent | undefined,
    spaceCopiedObjects:
      | {
          [key: string]: any;
        }[]
      | null,
    position?: PasteLocation,
  ): Promise<boolean> {
    const pasteLocation = position ?? this.getClipboardPasteLocation();

    // Handling pasting files from clipboard
    const files = event?.clipboardData?.files ?? (await this.readClipboardFiles());
    if (files?.length) {
      this.documentUploadService.addExternalFile(Array.from(files), pasteLocation);
      event?.preventDefault();
      return Promise.resolve(true);
    }

    const textClipboard =
      event?.clipboardData?.getData('text/plain') ?? (await this.readClipboardText());
    if (!textClipboard) {
      return false;
    }

    if (this.shouldFallbackToObjectPasteForTextbox(textClipboard, spaceCopiedObjects)) {
      return false;
    }

    // in case a postion is provided, we need to reset the object offset and last pointer
    // to avoid accumulating the offset when pasting multiple times
    if (position) {
      this.resetObjectOffset(position);
    }

    this.pasteNewObjectToCanvas(textClipboard, pasteLocation);

    event?.preventDefault();

    return true;
  }

  private getClipboardPasteLocation(): PasteLocation {
    const pasteLocation = this.zoomUtil.getCenterCoord();
    // If the user's pointer is not over the whiteboard canvas, fallback to the center of the VPT
    if (!this.sharedDataService.isPointerOverWhiteboardCanvas) {
      return pasteLocation;
    }
    if (this.sharedDataService.lastMouseLocation?.pointer) {
      pasteLocation.x = this.sharedDataService.lastMouseLocation.pointer.x;
      pasteLocation.y = this.sharedDataService.lastMouseLocation.pointer.y;
    }
    if (pasteLocation.x !== this.lastPointer.x || pasteLocation.y !== this.lastPointer.y) {
      this.resetObjectOffset(pasteLocation);
    }
    return pasteLocation;
  }

  private resetObjectOffset(pasteLocation: PasteLocation): void {
    this.objectOffset = { x: 0, y: 0 };
    this.lastPointer = pasteLocation;
  }

  private async readClipboardText(): Promise<string | undefined> {
    if (!navigator.clipboard?.readText) {
      return undefined;
    }

    try {
      const text = await navigator.clipboard.readText();
      return text || undefined;
    } catch {
      return undefined;
    }
  }

  private async readClipboardFiles(): Promise<File[]> {
    if (!(navigator.clipboard as any)?.read) {
      return [];
    }

    try {
      const clipboardItems = await (navigator.clipboard as any).read();
      const files: File[] = [];
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types || []) {
          if (!type.startsWith('image/')) {
            continue;
          }

          const blob = await clipboardItem.getType(type);
          const extension = type.split('/')[1] || 'png';
          files.push(new File([blob], `clipboard-image.${extension}`, { type }));
        }
      }

      return files;
    } catch {
      return [];
    }
  }

  private shouldFallbackToObjectPasteForTextbox(
    textClipboard: string,
    spaceCopiedObjects:
      | {
          [key: string]: any;
        }[]
      | null,
  ): boolean {
    if (!(spaceCopiedObjects && spaceCopiedObjects.length === 1 && spaceCopiedObjects[0]?.text)) {
      return false;
    }

    /**
     * for some reason, when user copy text, in the browser default clipboard data (textClipboard),
     * lines are separated with '\r\n' while in the textbox object, line are separated with '\n'
     * so when we compare fabric object text with clipboard text to see if it's the same, the clipboard
     * text length is greater than fabric text and will return false
     * solution: replace all '\r\n' by '\n' and compare
     */
    const textboxReBrLines = String(spaceCopiedObjects[0].text).replace(/\r\n/g, '\n');
    const textClipboardReBrLines = textClipboard.replace(/\r\n/g, '\n');

    return textboxReBrLines === textClipboardReBrLines && spaceCopiedObjects[0].type === 'textbox';
  }

  insertPlainText(textClipboard: string, pasteLocation: PasteLocation): void {
    const newIText = new fabric.Textbox(textClipboard, {
      ...TextSettingsDirector.build(),
      top: pasteLocation.y + this.objectOffset.y,
      left: pasteLocation.x + this.objectOffset.x,
      padding: 10,
      isWrapping: true,
      width: 320,
    });
    // center the textbox in the paste location
    newIText.set({
      top: (newIText.top ?? 0) - (newIText.height ?? 0) / 2,
      left: (newIText.left ?? 0) - (newIText.width ?? 0) / 2,
    });

    this.objectOffset.x += 40;
    this.objectOffset.y += 40;

    this.sharedDataService.fabricCanvas?.add(newIText);
    this.sharedDataService.fabricCanvas?.setActiveObject(newIText);
  }

  pasteNewObjectToCanvas(textClipboard: string, pasteLocation: PasteLocation): void {
    const appInstanceId = this.iframeUtils.appTypeFromPastedLink(textClipboard);

    if (!appInstanceId) {
      this.insertPlainText(textClipboard, pasteLocation);
      return;
    }

    if (appInstanceId === CollaborativeApps.GOOGLE_SLIDES) {
      this.slidesImportFlowService.openFlowWithPastedUrl(textClipboard);
      return;
    }

    if (appInstanceId === CollaborativeApps.YOUTUBE) {
      void this.pasteYoutubeLink(textClipboard, pasteLocation);
      return;
    }

    const url = this.iframeUtils.addHttpsIfItDoesNotExist(textClipboard);
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.IFrame>(
      ItemModel.IFrame,
      {
        url,
        appInstanceId,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }

  private async pasteYoutubeLink(
    textClipboard: string,
    pasteLocation: PasteLocation,
  ): Promise<void> {
    const youtubeApp = await firstValueFrom(this.appConfigurationService.setYoutubeApp$());

    if (!youtubeApp) {
      this.insertPlainText(textClipboard, pasteLocation);
      return;
    }

    await this.appsInsertionService.insertYoutubeApp(
      youtubeApp,
      this.iframeUtils.addHttpsIfItDoesNotExist(textClipboard),
    );
  }

  private canCopy(): boolean {
    const isSessionViewAllowedForShortcuts = this.mainToolbarService.isTargetToolbarView();
    // Use unified shortcut skip check for target session view (includes session view and input area checks)
    if (
      this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcutsForSessionView(
        isSessionViewAllowedForShortcuts,
      )
    ) {
      return false;
    }

    if (!this.sharedDataService.lastMouseLocation) {
      return false;
    }

    return true;
  }

  // return false if the paste action in not on the target session view
  private shouldHandlePaste() {
    if (this.observerModeService.isObserverModeActive()) {
      return false;
    }
    const isSessionViewAllowedForShortcuts = this.mainToolbarService.isTargetToolbarView();
    // Use unified shortcut skip check for target session view (includes session view and input area checks)
    // Return true if we should handle paste (opposite of shouldSkip)
    return !this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcutsForSessionView(
      isSessionViewAllowedForShortcuts,
    );
  }

  private canPasteOnWhiteboard() {
    return this.sharedDataService.canModifySession();
  }

  private clearClipBoardDataIfNoTextBoxIsSelected(
    event: ClipboardEvent | undefined,
    activeObjects: fabric.Object[],
  ) {
    if (!event) {
      return;
    }
    const isTextBox =
      activeObjects.length === 1 && ['i-text', 'textbox'].includes(activeObjects[0].type as string); // SPAC-6703

    if (!isTextBox) {
      // SPAC-7134,
      event.clipboardData?.setData('Text', '');
      event.preventDefault();
    }
  }

  private handlePartialSelectionOfTextBoxIfSelected(
    event: ClipboardEvent | undefined,
    clonedAndAbsoluteObjects: FabricObject[],
  ) {
    if (!event) {
      return;
    }
    // copy only the selected text
    const { selectionStart, selectionEnd } = event.target as any;
    const selectedTextBox = clonedAndAbsoluteObjects[0] as any as fabric.Text;
    if (
      Number.isInteger(selectionStart) &&
      Number.isInteger(selectionEnd) &&
      selectionStart !== selectionEnd &&
      selectedTextBox &&
      selectedTextBox.type === 'textbox' &&
      selectedTextBox.text
    ) {
      selectedTextBox.set('text', selectedTextBox.text.substring(selectionStart, selectionEnd));
    }
  }

  private showCanNotPasteNotification() {
    if (this.sharedDataService.canModifySession()) {
      return;
    }
    const title = new IconMessageToasterElement(
      { icon: 'lock_outline', size: 16 },
      this.translate.instant('paste not allowed'),
    );
    const message = new IconMessageToasterElement(
      undefined,
      this.translate.instant('You cannot paste objects to a Space when it is locked by a host'),
    );
    const notificationData = new NotificationDataBuilder(INFOS.SPACE_LOCKED)
      .type(NotificationType.INFO)
      .style(ToasterPopupStyle.INFO)
      .topElements([title])
      .middleElements([message])
      .bottomElements([])
      .dismissable(false)
      .timeOut(5)
      .build();
    this.notificationToasterService.showNotification(notificationData);
  }
}
